import React, { useEffect, useState } from "react";
import { ReceiptService, Receipt } from "../services/receipt-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { ActiveReceiptTab } from "./active-receipt-tab";
import { InactiveReceiptTab } from "./inactive-receipt-tab";
import { ReceiptCreate } from "../components/receipt-create";
import { ReceiptDetail } from "../components/receipt-detail";

function useDebounce<T>(value: T, delay = 500): T {
  const [val, setVal] = useState(value);
  useEffect(() => { const t = setTimeout(() => setVal(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return val;
}

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string | number;
}> = ({ icon, iconBg, label, value }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  </div>
);

export default function ReceiptIndexPage() {
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [reloadKey, setReloadKey] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [viewItem, setViewItem] = useState<Receipt | null>(null);
  const [editItem, setEditItem] = useState<Receipt | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const debouncedSearch = useDebounce(search, 500);

  const { fetch: createReceipt } = ReceiptService.create();
  const { fetch: updateReceipt } = ReceiptService.update();
  const { fetch: countTab } = ReceiptService.countTab();

  useEffect(() => {
    (async () => {
      const params: any = {};
      if (debouncedSearch) params.key_search = debouncedSearch;
      if (typeFilter) params.type = Number(typeFilter);
      const res = await countTab(params);
      if (res && isSuccess(res)) {
        const d: any = res.data || {};
        setActiveCount(Number(d.active ?? d["1"] ?? 0));
        setInactiveCount(Number(d.inactive ?? d["0"] ?? 0));
      } else {
        setActiveCount(0); setInactiveCount(0);
      }
    })();
  }, [debouncedSearch, typeFilter]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50 p-6 font-sans">

      {/* --- HEADER --- */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-200">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Phiếu Thu Chi</h1>
            <p className="text-sm text-gray-500 mt-0.5">Theo dõi và quản lý các phiếu thu chi của doanh nghiệp</p>
          </div>
        </div>

        <button
          onClick={() => { setEditItem(null); setOpenForm(true); }}
          className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:shadow-xl hover:shadow-green-300 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>Tạo phiếu mới</span>
        </button>
      </div>

      {/* --- STATS CARDS --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          icon={
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          iconBg="bg-gradient-to-br from-green-500 to-emerald-600"
          label="Phiếu đang hoạt động"
          value={activeCount.toLocaleString()}
        />
        <StatCard
          icon={
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          iconBg="bg-gradient-to-br from-amber-500 to-orange-600"
          label="Phiếu tạm ngưng"
          value={inactiveCount.toLocaleString()}
        />
        <StatCard
          icon={
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          iconBg="bg-gradient-to-br from-blue-500 to-indigo-600"
          label="Tổng số phiếu"
          value={(activeCount + inactiveCount).toLocaleString()}
        />
      </div>

      {/* --- MAIN CARD --- */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">

        {/* --- TOOLBAR (Tabs & Filters) --- */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">

            {/* LEFT: TABS */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
              <button
                onClick={() => setTab("active")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  tab === "active"
                    ? "bg-white text-green-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${tab === "active" ? "bg-green-500" : "bg-gray-400"}`} />
                <span>Đang hoạt động</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                   tab === "active" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"
                }`}>
                  {activeCount}
                </span>
              </button>

              <button
                onClick={() => setTab("inactive")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  tab === "inactive"
                    ? "bg-white text-amber-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${tab === "inactive" ? "bg-amber-500" : "bg-gray-400"}`} />
                <span>Tạm ngưng</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                   tab === "inactive" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-500"
                }`}>
                  {inactiveCount}
                </span>
              </button>
            </div>

            {/* RIGHT: FILTERS */}
            <div className="flex flex-col sm:flex-row items-center gap-3">

              {/* 1. Loại Phiếu Select */}
              <div className="relative w-full sm:w-auto">
                 <select
                    className="appearance-none w-full sm:w-[180px] rounded-xl border border-gray-200 bg-gray-50 pl-4 pr-10 py-2.5 text-sm font-medium text-gray-700 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all cursor-pointer hover:bg-gray-100"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                 >
                    <option value="">Tất cả loại phiếu</option>
                    <option value="1">Phiếu Thu</option>
                    <option value="2">Phiếu Chi</option>
                 </select>
                 <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                 </div>
              </div>

              {/* 2. Search Input */}
              <div className="relative w-full sm:w-[280px]">
                 <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                 </svg>
                 <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tìm kiếm phiếu..."
                    className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all"
                 />
              </div>
            </div>
          </div>
        </div>

        {/* --- CONTENT AREA (TABLE) --- */}
        <div className="p-0">
          {tab === "active" ? (
            <ActiveReceiptTab
                key={reloadKey + "-active"}
                search={debouncedSearch}
                typeFilter={typeFilter}
                onEdit={(it) => { setEditItem(it); setOpenForm(true); }}
                onView={(it) => setViewItem(it)}
                onLoadedCount={(n) => setActiveCount(n)}
            />
          ) : (
            <InactiveReceiptTab
                key={reloadKey + "-inactive"}
                search={debouncedSearch}
                typeFilter={typeFilter}
                onEdit={(it) => { setEditItem(it); setOpenForm(true); }}
                onView={(it) => setViewItem(it)}
                onLoadedCount={(n) => setInactiveCount(n)}
            />
          )}
        </div>
      </div>

      {/* --- MODALS --- */}
      {openForm && (
        <ReceiptCreate
            open={openForm}
            item={editItem}
            onClose={() => setOpenForm(false)}
            onSave={async (payload) => {
                const res = editItem ? await updateReceipt(editItem.id, payload) : await createReceipt(payload);
                if (res && isSuccess(res)) {
                    showToast({ message: editItem ? "Cập nhật phiếu thành công" : "Tạo phiếu thành công", type: "success" });
                    setOpenForm(false); setEditItem(null); setReloadKey((k) => k + 1);
                } else {
                    showToast({ message: res?.message ?? "Không thể lưu phiếu", type: "error" });
                }
            }}
        />
      )}
      {viewItem && <ReceiptDetail open={!!viewItem} data={viewItem} onClose={() => setViewItem(null)} />}
    </div>
  );
}
